/** Focus the Unity iframe so WebGL keeps receiving pointer moves. */
let blockUnityFocus = false;

export function setBlockUnityFocus(blocked) {
  blockUnityFocus = !!blocked;
}

export function focusUnity(iframeRef) {
  if (blockUnityFocus) return;
  const iframe = iframeRef?.current;
  if (!iframe) return;
  try {
    iframe.focus({ preventScroll: true });
  } catch {
    iframe.focus();
  }
}

function hostRect(iframeRef) {
  const host = iframeRef?.current?.parentElement;
  return (
    host?.getBoundingClientRect() ??
    iframeRef?.current?.getBoundingClientRect() ??
    null
  );
}

/** Viewport-normalized → CSS pixels relative to the Unity clip host. */
export function mapNormToHost(iframeRef, normX, normY) {
  const rect = hostRect(iframeRef);
  if (!rect) {
    return {
      left: Number.isFinite(normX) ? normX * window.innerWidth : 0,
      top: Number.isFinite(normY) ? normY * window.innerHeight : 0,
    };
  }
  return {
    left: (Number.isFinite(normX) ? normX : 0.5) * rect.width,
    top: (Number.isFinite(normY) ? normY : 0.5) * rect.height,
  };
}

/** Client (page) coords → viewport-normalized relative to the Unity clip host. */
export function clientToNorm(iframeRef, clientX, clientY) {
  const rect = hostRect(iframeRef);
  if (!rect || rect.width <= 0 || rect.height <= 0) {
    return { normX: 0.5, normY: 0.5 };
  }
  return {
    normX: (clientX - rect.left) / rect.width,
    normY: (clientY - rect.top) / rect.height,
  };
}

/**
 * CSS-px scale for content-locked strokes: 1 ≈ thickness px when image width fills host.
 * Derived from pose image-% vs screen px (no Unity zoom channel).
 */
export function contentStrokeScale(poses, hostWidth) {
  if (!(hostWidth > 0) || !poses?.size) return 1;
  const pts = [];
  for (const p of poses.values()) {
    if (p?.percentX != null && p?.percentY != null) pts.push(p);
  }
  let bestD = 0;
  let scale = 1;
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const dPct = Math.hypot(
        pts[j].percentX - pts[i].percentX,
        pts[j].percentY - pts[i].percentY
      );
      if (dPct < 0.5 || dPct <= bestD) continue;
      const dScr = Math.hypot(
        pts[j].left - pts[i].left,
        pts[j].top - pts[i].top
      );
      bestD = dPct;
      scale = dScr / dPct / (hostWidth / 100);
    }
  }
  return scale;
}

/** Nominal slider thickness → screen stroke width that tracks pan/zoom. */
export function zoomScaledStroke(thickness, poses, hostWidth) {
  const t = Number(thickness);
  const base = Number.isFinite(t) ? t : 3;
  return Math.max(0.5, base * contentStrokeScale(poses, hostWidth));
}
